import { Link } from "react-router-dom";
import {
  ArrowRight,
  Compass,
  Target,
  HeartHandshake,
  ShieldCheck,
  Users2,
  TrendingUp,
  Briefcase,
  Lock,
  Lightbulb,
  GraduationCap,
  Sprout,
  Infinity as InfinityIcon,
  Quote,
  Globe2,
  Cpu,
  Award,
  LineChart,
  ScrollText,
  Building2,
} from "lucide-react";
import { PolicyHeader } from "@/components/PolicyHeader";
import { SITE_IMAGES } from "@/data/site-images";

const STRATEGIC_MISSION = [
  { icon: Globe2, t: "Financial Inclusion", d: "Responsible and accessible financial services to every deserving individual, entrepreneur, farmer, startup, MSME, and corporate organization — regardless of geography or background." },
  { icon: TrendingUp, t: "Wealth Creation", d: "Help customers build, preserve, grow, and transfer wealth through disciplined investment planning, diversified products, and professional advisory." },
  { icon: Briefcase, t: "Business Growth", d: "Support businesses through project finance, working capital, equipment finance, expansion funding, restructuring advisory, and strategic planning." },
  { icon: Cpu, t: "Digital Innovation", d: "Leverage AI, automation, cloud, advanced analytics, and cybersecurity to deliver intelligent financial solutions." },
  { icon: ShieldCheck, t: "Responsible Lending", d: "Promote transparent, ethical, and sustainable lending while protecting customer interests with strong risk-management standards." },
  { icon: Building2, t: "Corporate Advisory", d: "Comprehensive guidance across finance, taxation, legal documentation, compliance, accounting, restructuring, and business strategy." },
  { icon: Lock, t: "Risk Management", d: "Strong governance frameworks, compliance systems, cybersecurity, fraud prevention, and enterprise risk processes." },
  { icon: HeartHandshake, t: "Customer Excellence", d: "Personalized financial solutions supported by exceptional service, expertise, transparency and long-term relationship management." },
  { icon: GraduationCap, t: "Human Capital Development", d: "Opportunities for employees, advisors, DSAs, associate partners, agencies, and entrepreneurs through education, leadership and certification." },
  { icon: Sprout, t: "Social Responsibility", d: "Promote financial literacy, entrepreneurship, employment, women empowerment, youth development, tribal welfare, and environmental sustainability." },
];

const PHILOSOPHY_LINES = [
  "Finance is about creating opportunities.",
  "Finance is about transforming ideas into successful businesses.",
  "Finance is about helping families achieve financial security.",
  "Finance is about empowering entrepreneurs to build industries.",
  "Finance is about enabling investors to create long-term wealth.",
  "Finance is about supporting communities and strengthening the nation's economy.",
];

const LONG_TERM_GOALS_2035 = [
  ["1M+", "Customers served"],
  ["₹10,000+", "Crore in financial solutions"],
  ["Nationwide", "Presence across every state & major district"],
  ["AI-powered", "Digital financial platforms"],
  ["Largest", "DSA · Associate · Agency network"],
  ["World-class", "Governance, compliance & security"],
];

const CORE_VALUES = [
  "Integrity Beyond Compliance",
  "Customer First",
  "Trust Through Transparency",
  "Innovation with Responsibility",
  "Excellence Without Compromise",
  "Sustainable Wealth Creation",
  "Accountability at Every Level",
  "Respect for Every Stakeholder",
  "Professional Excellence",
  "Continuous Learning",
  "Digital Innovation",
  "Nation Building Through Financial Empowerment",
];

const BRAND_PROMISE = [
  "We build businesses.",
  "We create wealth.",
  "We strengthen families.",
  "We empower entrepreneurs.",
  "We enable financial freedom.",
  "We create opportunities.",
];

function Section({ eyebrow, title, lead, children, testId }) {
  return (
    <section data-testid={testId} className="py-16 md:py-20 border-t border-white/5">
      <div className="max-w-6xl mx-auto px-5 md:px-8">
        {eyebrow && (
          <div className="text-[11px] uppercase tracking-[0.3em] text-[#D4AF37] mb-3">
            {eyebrow}
          </div>
        )}
        {title && (
          <h2 className="font-serif text-3xl md:text-4xl text-white max-w-3xl leading-tight">
            {title}
          </h2>
        )}
        {lead && (
          <p className="mt-4 text-base text-white/75 max-w-3xl leading-relaxed">{lead}</p>
        )}
        <div className="mt-10">{children}</div>
      </div>
    </section>
  );
}

export default function VisionMission() {
  return (
    <div className="min-h-screen bg-[#04102B] text-white">
      <PolicyHeader
        eyebrow="Vision & Mission"
        title={
          <>
            Building India's Most Trusted{" "}
            <em className="not-italic text-[#D4AF37]">Integrated Financial</em> &amp;{" "}
            <span className="font-script italic text-[#F0D788]">Wealth Management</span> Ecosystem
          </>
        }
        lead="Sahyog Finance & Noble Infinity Asset Management — a long-term ambition to empower individuals, families, entrepreneurs, professionals, farmers, startups, MSMEs and corporations to achieve sustainable financial prosperity."
      />

      {/* OUR VISION */}
      <Section
        testId="vision-section"
        eyebrow="Our Vision"
        title="A future where financial services are accessible, transparent, technology-driven and built around every customer"
      >
        <div className="grid lg:grid-cols-[1.1fr_1fr] gap-8 items-start">
          <div className="space-y-5 text-base text-white/80 leading-relaxed">
            <p>
              Our vision is to become one of India's most trusted, innovative and customer-centric
              integrated financial services organizations — delivering world-class financial
              solutions that empower individuals, families, entrepreneurs, professionals, farmers,
              startups, MSMEs, corporations and institutions to achieve sustainable financial
              prosperity.
            </p>
            <p>
              Our long-term ambition is to establish a diversified financial institution that
              seamlessly integrates Project Finance, Business Finance, Personal Finance, Investment
              Advisory, Wealth Management, Mutual Funds, Corporate Financial Services, Insurance
              Solutions, Taxation, Legal Consultancy, Compliance Management, Business Strategy and
              Digital Financial Services under one trusted brand.
            </p>
            <p>
              We aspire to redefine the standards of financial excellence by embracing Artificial
              Intelligence, Digital Banking Technologies, Data Analytics, Cybersecurity, Automation
              and Customer Intelligence — to deliver faster, safer and smarter financial solutions
              while maintaining the highest standards of integrity, transparency, accountability,
              governance and regulatory compliance.
            </p>
            <p>
              Beyond financial success, we aim to become a catalyst for national development by
              promoting entrepreneurship, supporting rural and tribal economies, strengthening
              MSMEs, encouraging responsible investing, improving financial literacy, generating
              employment opportunities, and contributing to India's vision of becoming a global
              economic leader.
            </p>
          </div>
          <div className="lg:sticky lg:top-24">
            <div className="relative rounded-3xl overflow-hidden border border-[#D4AF37]/25 shadow-[0_20px_60px_-20px_rgba(0,0,0,0.6)]">
              <img
                src={SITE_IMAGES.heroes[1]}
                alt="A vision for India's financial future"
                loading="lazy"
                data-testid="vision-hero-image"
                className="w-full h-[280px] md:h-[420px] object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#04102B]/80 via-transparent to-transparent pointer-events-none" />
            </div>
          </div>
        </div>
      </Section>

      {/* OUR MISSION */}
      <Section
        testId="mission-section"
        eyebrow="Our Mission"
        title="Empowering every financial journey through trust, innovation & excellence"
        lead="Provide comprehensive, transparent, technology-driven, and customer-focused financial solutions that enable individuals, businesses, entrepreneurs, professionals, startups, farmers and corporates to achieve their financial aspirations with confidence, security and long-term sustainability."
      >
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {STRATEGIC_MISSION.map((m, i) => (
            <div
              key={m.t}
              data-testid={`mission-pillar-${i}`}
              className="rounded-2xl border border-[#D4AF37]/15 bg-[#0A1F44] p-6 hover:border-[#D4AF37]/45 transition"
            >
              <div className="w-10 h-10 grid place-items-center rounded-lg bg-[#D4AF37]/10 border border-[#D4AF37]/25 mb-4">
                <m.icon size={18} className="text-[#F0D788]" />
              </div>
              <h3 className="font-serif text-lg text-white mb-2">{m.t}</h3>
              <p className="text-sm text-white/70 leading-relaxed">{m.d}</p>
            </div>
          ))}
        </div>
      </Section>

      {/* CORE PURPOSE */}
      <Section testId="core-purpose-section" eyebrow="Our Core Purpose">
        <div className="rounded-2xl border border-[#D4AF37]/25 bg-gradient-to-br from-[#0A1F44] via-[#0c2654] to-[#04102B] p-8 md:p-12 max-w-4xl">
          <Target size={28} className="text-[#D4AF37] mb-5" />
          <p className="font-serif text-xl md:text-3xl leading-snug text-white">
            To empower individuals, families, businesses, and institutions with{" "}
            <span className="text-[#F0D788]">intelligent financial solutions</span> that create
            sustainable wealth, accelerate economic growth, and improve quality of life through
            trust, innovation and professional excellence.
          </p>
        </div>
      </Section>

      {/* CORPORATE PHILOSOPHY */}
      <Section
        testId="philosophy-section"
        eyebrow="Our Corporate Philosophy"
        title="Finance is not merely about lending money or managing investments"
      >
        <div className="grid md:grid-cols-2 gap-3 max-w-4xl">
          {PHILOSOPHY_LINES.map((line, i) => (
            <div
              key={`phil-${i}-${String(line).slice(0, 24)}`}
              data-testid={`philosophy-line-${i}`}
              className="flex items-start gap-3 rounded-xl border border-white/10 bg-white/[0.02] px-5 py-4"
            >
              <Sprout size={16} className="text-[#D4AF37] mt-1 shrink-0" />
              <div className="text-sm md:text-base text-white/85">{line}</div>
            </div>
          ))}
        </div>
        <p className="mt-8 text-sm text-white/65 italic max-w-3xl">
          Every decision we make is guided by integrity, transparency, innovation, customer
          success and sustainable value creation.
        </p>
      </Section>

      {/* LONG-TERM GOALS 2035 */}
      <Section
        testId="goals-section"
        eyebrow="Long-Term Goals · 2035"
        title="What we are building over the next decade"
      >
        <div className="mb-8 rounded-3xl overflow-hidden border border-[#D4AF37]/20">
          <img
            src={SITE_IMAGES.heroes[3]}
            alt="Long-term vision · 2035 milestones"
            loading="lazy"
            data-testid="goals-image"
            className="w-full h-[180px] md:h-[240px] object-cover"
          />
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {LONG_TERM_GOALS_2035.map(([n, label], i) => (
            <div
              key={`goal-${n}-${i}`}
              data-testid={`goal-${i}`}
              className="rounded-2xl border border-[#D4AF37]/25 bg-[#0A1F44] p-6"
            >
              <div className="font-serif text-3xl text-[#F0D788]">{n}</div>
              <div className="mt-2 text-sm text-white/80">{label}</div>
            </div>
          ))}
        </div>
      </Section>

      {/* CORE VALUES */}
      <Section testId="values-section" eyebrow="Our Core Values" title="Twelve principles that guide every decision">
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {CORE_VALUES.map((v, i) => (
            <div
              key={v}
              data-testid={`value-${i}`}
              className="flex items-center gap-3 rounded-xl border border-white/10 bg-white/[0.02] px-4 py-3"
            >
              <Award size={14} className="text-[#D4AF37] shrink-0" />
              <div className="text-sm text-white/85">{v}</div>
            </div>
          ))}
        </div>
      </Section>

      {/* BRAND PROMISE */}
      <Section testId="promise-section" eyebrow="Our Brand Promise" title="We do not simply provide financial services">
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3 max-w-5xl">
          {BRAND_PROMISE.map((line, i) => (
            <div
              key={`promise-${i}-${String(line).slice(0, 24)}`}
              data-testid={`promise-${i}`}
              className="rounded-xl border border-[#D4AF37]/20 bg-gradient-to-br from-[#0A1F44] to-[#04102B] px-5 py-4"
            >
              <div className="font-serif text-base text-white">{line}</div>
            </div>
          ))}
        </div>
        <p className="mt-8 text-sm text-white/65 italic">
          We build lifelong partnerships based on trust, transparency, innovation and excellence.
        </p>
      </Section>

      {/* TAGLINE */}
      <Section testId="tagline-section" eyebrow="Our Tagline">
        <div className="rounded-2xl border border-[#D4AF37]/30 bg-gradient-to-br from-[#0A1F44] via-[#0c2654] to-[#04102B] p-8 md:p-14 text-center max-w-3xl mx-auto">
          <Quote size={26} className="text-[#D4AF37] mx-auto mb-4" />
          <div className="font-serif text-3xl md:text-5xl text-white leading-tight">
            Finance Today.{" "}
            <span className="font-script italic text-[#F0D788]">Prosper Tomorrow.</span>
          </div>
        </div>

        <div className="mt-10 flex flex-wrap items-center justify-center gap-3">
          <Link
            to="/apply/loan"
            data-testid="vision-apply-cta"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-full btn-gold text-sm font-semibold"
          >
            Apply for a Loan <ArrowRight size={14} />
          </Link>
          <Link
            to="/investors"
            data-testid="vision-investors-cta"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-full btn-outline-gold text-sm"
          >
            Investor Relations
          </Link>
        </div>
      </Section>
    </div>
  );
}
